import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { LogIn, Lock, ArrowRight } from "lucide-react";

export default function ClientLogin() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const next = params.get("next") || "/area-cliente";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/login", { email, password });
      await refresh();
      toast.success("Accesso effettuato");
      nav(next);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-16 bg-bg">
      <form
        onSubmit={submit}
        data-testid="client-login-form"
        className="w-full max-w-md bg-white border border-line rounded-lg shadow-card p-8"
      >
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand/10 text-brand rounded-full text-[11px] font-display uppercase tracking-[0.2em]">
          <Lock className="w-3 h-3" /> Area cliente
        </div>
        <h1 className="mt-3 font-display text-3xl text-ink">
          Accedi al <span className="text-brand">tuo dossier</span>
        </h1>
        <p className="mt-2 text-sm text-ink-muted">
          Serve solo per la check list documenti. Il calcolatore resta libero e senza registrazione.
        </p>

        <div className="mt-8 space-y-4">
          <label className="block">
            <span className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">
              Email
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="login-email"
              autoComplete="username"
              className="w-full h-11 px-3 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm"
            />
          </label>
          <label className="block">
            <span className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">
              Password
            </span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password"
              autoComplete="current-password"
              className="w-full h-11 px-3 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm"
            />
          </label>
        </div>

        <button
          type="submit"
          disabled={loading}
          data-testid="login-submit"
          className="mt-6 w-full h-11 bg-brand text-white font-display uppercase tracking-widest text-sm rounded-md hover:bg-brand-dark hover:shadow-brand-glow disabled:opacity-60 flex items-center justify-center gap-2"
        >
          <LogIn className="w-4 h-4" />
          {loading ? "Accesso..." : "Accedi"}
        </button>

        <p className="mt-4 text-center">
          <Link
            to="/password-dimenticata"
            data-testid="link-forgot-password"
            className="text-sm text-brand hover:underline"
          >
            Hai dimenticato la password?
          </Link>
        </p>

        <p className="mt-6 text-sm text-ink-muted text-center">
          Non hai ancora un account?{" "}
          <Link
            to={`/registrati${next && next !== "/area-cliente" ? `?next=${encodeURIComponent(next)}` : ""}`}
            data-testid="link-register"
            className="text-brand hover:underline inline-flex items-center gap-1"
          >
            Registra la tua azienda <ArrowRight className="w-3 h-3" />
          </Link>
        </p>

        <p className="mt-6 text-xs text-ink-muted text-center">
          <Link to="/admin/login" className="hover:text-brand">
            Accesso amministratore →
          </Link>
        </p>
      </form>
    </div>
  );
}
